import { Box, Typography } from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import { debounce, decodedJWT, getUserRole } from '../../utils/commonUtils';
import Coverage, { CoverageStatus } from '../../interfaces/coverage';
import getCoverageColDef from '../../colDef/coverageColumnDef';
import SearchResultGrid from '../searchResultGrid/searchResultGrid';
import { createFilters, getSelectedFiltersCount } from '../../utils/filterUtils';
import Picklist from '../../interfaces/picklist';
import Filter from '../../interfaces/filters';
import FilterDrawer from '../dashboard/filterDrawer';
import { GridPaginationModel, GridRowSelectionModel } from '@mui/x-data-grid';
import CoverageService from '../../services/coverageService';
import Loader from '../baseComponents/loader';
import CoverageToolbar from './coverageToolbar';
import CoverageGridActions from './coverageGridActions';
import JWTTokenPayload from '../../interfaces/JWTTokenPayload';
import RoleManagement from '../../security/roleManagement';
import { useSelector } from 'react-redux';
import { RootState } from '../../store/store';

const filterKeys = ['status', 'user_lookup', 'pin_code', 'state', 'district'];

const CoverageManagement: React.FC = () => {
    const token: false | JWTTokenPayload = decodedJWT();
    const userRole = getUserRole(token);
    const [coverages, setCoverages] = useState<Coverage[]>([]);
    const [coveragesCount, setCoveragesCount] = useState<number>(0);
    const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({ page: 0, pageSize: 40 });
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isFiltersOpen, setIsFiltersOpen] = useState<boolean>(false);
    const [filters, setFilters] = useState<Filter[]>([]);
    const [filtersCount, setFiltersCount] = useState<number>(0);
    const [leftMargin, setLeftMargin] = useState<string>('16px');
    const [selectedRows, setSelectedRows] = useState<Coverage[]>([]);
    const [rowSelectionModel, setRowSelectionModel] = useState<GridRowSelectionModel>({ type: 'include', ids: new Set<string | number>() });
    
    const canManage = RoleManagement.isSupervisor(userRole) || RoleManagement.isAdmin(userRole);
    
    // Get states and districts from Redux store
    const districtAndStates = useSelector((state: RootState) => state.districtsAndStates);
    const states = districtAndStates.states;
    const districts = useMemo(() => districtAndStates.districts.map((val) => val.district), [districtAndStates.districts]);
    const users = useSelector((state: RootState) => state.userList.users);

    const finalColumnDef = getCoverageColDef();

    const handleSelectRow = (ids: GridRowSelectionModel) => {
        setRowSelectionModel(ids);
        const selectedCoverages = coverages.filter((coverage) => 
            Array.from(ids.ids).includes(coverage.id)
        );
        setSelectedRows(selectedCoverages);
    };

    const clearSelection = () => {
        setSelectedRows([]);
        setRowSelectionModel({ type: 'include', ids: new Set<string | number>() });
    };

    const handleActionSuccess = () => {
        clearSelection();
        fetchCoverage();
    };

    // Initialize filters when Redux data is available
    useEffect(() => {
        const statusPicklist = new Picklist(
            [],
            [CoverageStatus.PENDING_REVIEW, CoverageStatus.APPROVED, CoverageStatus.REJECTED, CoverageStatus.REMOVED],
            [],
            [],
            [],
            [],
            []
        );
        
        const initialFilters = createFilters(
            filterKeys, 
            [],
            [],
            [],
            statusPicklist, 
            states,
            districts,
            users,
            []
        );
        
        const statusFilter = initialFilters.find(f => f.id === 'status');
        if (statusFilter) {
            statusFilter.value = [CoverageStatus.PENDING_REVIEW];
        }
        
        setFilters(initialFilters);
    }, [states, districts, users]);

    const fetchCoverage = async () => {
        // Check if we have actual filter values (not just default empty filters)
        const hasFilterValues = filters.some(f => {
            if (Array.isArray(f.value)) {
                return f.value.length > 0;
            }
            return f.value !== '' && f.value !== null && f.value !== undefined;
        });

        try {
            setIsLoading(true);
            let page = paginationModel.page + 1;
            let pageSize = paginationModel.pageSize;
            
            // If no filters selected, use page=1 and page_size=100
            if (!hasFilterValues) {
                page = 1;
                pageSize = 100;
            }
            
            const response = await CoverageService.getCoverageList(filters, page, pageSize);
            if (response) {
                setCoverages(response.coverage_entries);
                setCoveragesCount(response.count);
            }
        } catch (error) {
            console.error('Error fetching coverage:', error);
            setCoverages([]);
            setCoveragesCount(0);
        } finally {
            setIsLoading(false);
        }
    };

    const debouncedFunc = useMemo(() => debounce(fetchCoverage, 500), [filters, paginationModel]);

    useEffect(() => {
        debouncedFunc.cancel();
        debouncedFunc();
        return () => {
            debouncedFunc.cancel();
        };
    }, [filters, paginationModel, debouncedFunc]);

    useEffect(() => {
        setFiltersCount(getSelectedFiltersCount(filters));
        clearSelection();
    }, [filters]);

    const toggleFilterDrawer = () => {
        setIsFiltersOpen(!isFiltersOpen);
        setLeftMargin(isFiltersOpen ? "16px" : "166px");
    };

    const resetFilters = () => {
        const statusPicklist = new Picklist(
            [],
            [CoverageStatus.PENDING_REVIEW, CoverageStatus.APPROVED, CoverageStatus.REJECTED, CoverageStatus.REMOVED],
            [],
            [],
            [],
            [],
            []
        );
        
        const resetFilters = createFilters(
            filterKeys, 
            [],
            [],
            [],
            statusPicklist, 
            states,
            districts,
            users,
            []
        );
        
        const statusFilter = resetFilters.find(f => f.id === 'status');
        if (statusFilter) {
            statusFilter.value = [CoverageStatus.PENDING_REVIEW];
        }
        
        setFilters(resetFilters);
        setPaginationModel({ page: 0, pageSize: 40 });
    };

    return (
        <Box sx={{ ml: leftMargin, mt: 2, mr: 2, mb: 2 }}>
            <FilterDrawer
                filters={filters}
                setFilters={setFilters}
                drawerOpen={isFiltersOpen}
                toggleDrawer={toggleFilterDrawer}
            />
            <Typography variant="h5">Serviceable Territory</Typography>
            <CoverageToolbar
                filtersCount={filtersCount}
                resetFilters={resetFilters}
                toggleFilterDrawer={toggleFilterDrawer}
            />
            {canManage && (
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "flex-end",
                        mb: 2,
                    }}
                >
                    <CoverageGridActions selectedCoverages={selectedRows} onSuccess={handleActionSuccess} />
                </Box>
            )}
            {isLoading ? (
                <Loader color="primary" />
            ) : (
                <SearchResultGrid
                    getRowId={(row: Coverage) => row.id}
                    results={coverages}
                    rowCount={coveragesCount}
                    onSelectRow={handleSelectRow}
                    columns={finalColumnDef}
                    paginationModel={paginationModel}
                    setPaginationModel={setPaginationModel}
                    disableDownloadAsCSVOptions
                    disableDownloadByPrintOptions
                    isRowSelectionAllowed={canManage}
                    rowSelectionModel={rowSelectionModel}
                />
            )}
        </Box>
    );
};

export default CoverageManagement;
